import { forwardRef, type CSSProperties, type HTMLAttributes } from 'react'

export type GridSize = number | 'auto' | true | false
export type GridSpacing = number | string

export interface GridSizeMap {
  xs?: GridSize
  sm?: GridSize
  md?: GridSize
  lg?: GridSize
  xl?: GridSize
}

export interface GridProps extends HTMLAttributes<HTMLDivElement> {
  container?: boolean
  item?: boolean
  spacing?: GridSpacing
  /** MUI v2 size prop — shorthand for xs/sm/md/lg/xl */
  size?: GridSizeMap
  xs?: GridSize
  sm?: GridSize
  md?: GridSize
  lg?: GridSize
  xl?: GridSize
  direction?: CSSProperties['flexDirection']
  justifyContent?: CSSProperties['justifyContent']
  alignItems?: CSSProperties['alignItems']
  wrap?: CSSProperties['flexWrap']
}

const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'] as const

export const Grid = forwardRef<HTMLDivElement, GridProps>(
  (
    {
      container,
      item,
      spacing = 0,
      size,
      xs: xsProp,
      sm: smProp,
      md: mdProp,
      lg: lgProp,
      xl: xlProp,
      direction,
      justifyContent,
      alignItems,
      wrap = 'wrap',
      className,
      style,
      ...rest
    },
    ref
  ) => {
    const bpValues: Record<string, GridSize | undefined> = {
      xs: size?.xs ?? xsProp,
      sm: size?.sm ?? smProp,
      md: size?.md ?? mdProp,
      lg: size?.lg ?? lgProp,
      xl: size?.xl ?? xlProp,
    }

    const halfGap = typeof spacing === 'number' ? (spacing * 8) / 2 : undefined

    // Any non-container Grid is implicitly an item (gets spacing from parent container)
    const isItem =
      !container ||
      item ||
      size != null ||
      xsProp != null ||
      smProp != null ||
      mdProp != null ||
      lgProp != null ||
      xlProp != null

    const computedStyle: CSSProperties & Record<string, unknown> = {
      ...style,
      ...(container
        ? {
            display: 'flex',
            flexWrap: wrap,
            boxSizing: 'border-box',
            ...(halfGap
              ? {
                  width: `calc(100% + ${halfGap * 2}px)`,
                  margin: -halfGap,
                  '--grid-half-gap': `${halfGap}px`,
                }
              : undefined),
            ...(direction ? { flexDirection: direction } : undefined),
            ...(justifyContent ? { justifyContent } : undefined),
            ...(alignItems ? { alignItems } : undefined),
          }
        : undefined),
      ...(isItem ? { padding: 'var(--grid-half-gap, 0px)' } : undefined),
    }

    // Build CSS classes for responsive breakpoints
    const bpClasses: string[] = []
    if (isItem) {
      for (const bp of breakpoints) {
        const val = bpValues[bp]
        if (val == null) {
          continue
        }
        if (val === true) {
          bpClasses.push(`tuna-grid--${bp}-grow`)
        } else if (val === 'auto') {
          bpClasses.push(`tuna-grid--${bp}-auto`)
        } else if (val === false) {
          bpClasses.push(`tuna-grid--${bp}-hidden`)
        } else {
          bpClasses.push(`tuna-grid--${bp}-${val}`)
        }
      }
    }

    const classes = ['tuna-grid', container ? 'tuna-grid--container' : undefined, ...bpClasses, className]
      .filter(Boolean)
      .join(' ')

    return <div ref={ref} className={classes} style={computedStyle} {...rest} />
  }
)

Grid.displayName = 'Grid'
